/**
 * Reusable marketing banner rule resolver.
 *
 * Supported rule key format:
 * - slug_<slugValue>_auth_<loginState>
 * - slug_<slugValue>
 * - auth_<loginState>
 * - ALL
 *
 * loginState values:
 * - loggedin
 * - guest
 *
 * Example:
 * slug_amexgold_auth_loggedin
 */
const LOGIN_STATES = {
  LOGGED_IN: 'loggedin',
  GUEST: 'guest',
};

const normalizeType = (value = '') => (typeof value === 'string' ? value.trim().toLowerCase() : '');

const buildBannerTypeKey = ({ userCardType = '', isLoggedIn = false } = {}) => {
  const sanitizedCardType = normalizeType(userCardType).replace(/\s+/g, '');
  const loginState = isLoggedIn ? LOGIN_STATES.LOGGED_IN : LOGIN_STATES.GUEST;
  if (!sanitizedCardType) return '';
  return `slug_${sanitizedCardType}_auth_${loginState}`;
};

const buildBannerTypeKeys = ({ userCardType = '', isLoggedIn = false } = {}) => {
  const sanitizedCardType = normalizeType(userCardType).replace(/\s+/g, '');
  const loginState = isLoggedIn ? LOGIN_STATES.LOGGED_IN : LOGIN_STATES.GUEST;
  const typeKeys = [];

  if (sanitizedCardType) {
    typeKeys.push(`slug_${sanitizedCardType}_auth_${loginState}`);
    typeKeys.push(`slug_${sanitizedCardType}`);
  }

  typeKeys.push(`auth_${loginState}`);
  typeKeys.push('all');

  return typeKeys;
};

const resolveBannerRule = (rules = [], activeTypeKey = '') => {
  if (!Array.isArray(rules) || rules.length === 0) return null;
  const typeKeys = Array.isArray(activeTypeKey) ? activeTypeKey : [activeTypeKey];
  const normalizedKeys = typeKeys.map((key) => normalizeType(key)).filter(Boolean);
  for (let i = 0; i < normalizedKeys.length; i += 1) {
    const matchedRule = rules.find((rule) => normalizeType(rule?.type) === normalizedKeys[i]);

    if (matchedRule) return matchedRule;
  }
  return null;
};

const isRuleBasedBanner = (bannerConfig = {}) => (
  Array.isArray(bannerConfig?.contents) || Array.isArray(bannerConfig?.images)
);

const resolveBannerConfig = ({ bannerConfig = {}, userCardType = '', isLoggedIn = false } = {}) => {
  if (!bannerConfig || Object.keys(bannerConfig).length === 0) return {};
  if (!isRuleBasedBanner(bannerConfig)) return bannerConfig;

  const activeTypeKeys = buildBannerTypeKeys({ userCardType, isLoggedIn });

  const contentRule = resolveBannerRule(bannerConfig.contents, activeTypeKeys);
  const imageRule = resolveBannerRule(bannerConfig.images, activeTypeKeys);

  const {
    header = '',
    copy = '',
    fine_print: finePrint = '',
  } = contentRule || {};

  const resolvedImages = imageRule?.image || {};
  const resolvedConfig = {
    ...bannerConfig,
    header,
    copy,
    fine_copy: finePrint,
    images: Object.keys(resolvedImages).length > 0 ? resolvedImages : bannerConfig.images,
    image_alt: (imageRule && Object.prototype.hasOwnProperty.call(imageRule, 'image_alt'))
      ? imageRule.image_alt
      : bannerConfig.image_alt,
  };

  return resolvedConfig;
};

export {
  buildBannerTypeKey,
  buildBannerTypeKeys,
  resolveBannerRule,
  resolveBannerConfig,
};
